import Vue from "vue";
import Vuex from "vuex";
import auth from "@/services/auth";
import account from "@/services/account";
import hasFlag from "@/utils/hasFlag";
Vue.use(Vuex);

export default {
  namespaced: true,
  state: {
    token: null,
    user: null,
    permissionsLastUpdate: null,
    permissions: {
      admin: {
        permissions: [],
        permissionsGroups: {},
      }
    },
    companies: [],
    cacheUser: false,
  },
  getters: {
    isAuthenticated: (state) => state.token,
    user: (state) => state.user,
    userFirstName: (state) => state.user.name.split(" ")[0],
    userPermissions: (state) => state.user?.permissions || [],
    permissions: (state, _, rootState) => {
      if (!rootState.platform.selectedPlatform) return state.permissions.admin;
      return state.permissions.platform;
    },
    permissionsGroups: (state) => state.permissions.admin.permissionsGroups,
    cacheUser: (state) => state.cacheUser,
    hasPermission: (state, getters, rootState) => (permission) => {
      const selectedCompany = rootState.company.selectedCompany;
      const companyPermissions = getters.userPermissions.find(
        (up) => up.companyId === selectedCompany
      );
      if (!companyPermissions) return false;

      if (Array.isArray(permission)) {
        return permission.some((p) => verifyPermission(p, companyPermissions));
      } else {
        return verifyPermission(permission, companyPermissions);
      }
    },
    hasUltimatePermission: (state, getters) => (permission) => {
      const ultimatePermission = getters.userPermissions.find(
        (up) => up.companyId === "_ultimate"
      );
      if (!ultimatePermission) return false;
      if (permission === "*") return true;

      if (Array.isArray(permission)) {
        return permission.some((p) => verifyPermission(p, ultimatePermission));
      } else {
        return verifyPermission(permission, ultimatePermission);
      }
    },
  },
  mutations: {
    setLogin(state, { token, user, companies }) {
      state.token = token;
      state.user = user;
      state.companies = companies;
      if (!!user)
        state.cacheUser = {
          id: user.id,
          name: user.name,
          phone: user.phone,
          photo: user.photo,
          ddi: user.ddi,
        };
    },
    setPermissions(state, permissions) {
      state.permissions = permissions;
      state.permissionsLastUpdate = new Date();
    },
    clearCacheUser(state) {
      state.cacheUser = false;
    },
    setUserPhoto(state, photo) {
      state.user.photo = photo;
    },
  },
  actions: {
    setUserPhoto({ commit }, photo) {
      commit("setUserPhoto", photo);
    },
    async setLogin({ commit }, { token, user, authenticators = [], companies = [] }) {
      commit("setLogin", { token, user, authenticators, companies });
    },
    async login({ commit }, data) {
      let response = await auth.login(data);
      commit("setLogin", response);
      return response;
    },
    async renewToken({ commit, state, dispatch }) {
      try {
        let response = await auth.renewToken(state.token);
        commit("setLogin", response);
        return response;
      } catch (e) {
        if (!navigator.onLine
          || e.message === "Network Error"
        ) return true;

        console.log("Renew Token error:", e);
        await dispatch("logout");
        return false;
      }
    },
    async getPermissions({ commit, state, getters }) {
      if (
        state.permissionsLastUpdate &&
        new Date(state.permissionsLastUpdate) > new Date() - 1000 * 60 * 60 * 12
      )
        return getters.permissions;

      let response = await auth.permissions();
      commit("setPermissions", response);
      return getters.permissions;
    },
    async logout({ commit, state, dispatch }) {
      dispatch("app/logout", null, { root: true });
      commit("setLogin", { token: null, user: null, companies: [] });
    },
    clearCacheUser({ commit }) {
      commit("clearCacheUser");
    },
  },
};

const verifyPermission = (permission, orgPermissions) =>
  (orgPermissions.permission & permission) === permission;
